import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';

import type { PageQuery, PagedResult } from '@domain/models/paged-result.model';
import type {
  Store,
  StoreFilter,
  StoreLightUpdate,
  StoreOrder,
  StorePriority,
  StoreSignActionUpdate,
  StoreSignUser,
  StoreVoucherUsage,
} from '@domain/models/store.model';
import { StoreRepository } from '@domain/repositories/store.repository';

import type { ApiPagedResponse } from '@data/dto/api.dto';
import type {
  StoreDto,
  StoreOrderDto,
  StorePriorityDto,
  StoreSignUserDto,
  StoreVoucherUsageDto,
} from '@data/dto/store.dto';
import {
  applyStoreFilter,
  mapPagedPriorities,
  mapPagedSignUsers,
  mapPagedStoreOrders,
  mapPagedStores,
  mapStoreDto,
  mapStoreVoucherUsageDto,
  storeQueryParams,
} from '@data/mappers/store.mapper';

@Injectable()
export class StoreApiRepository extends StoreRepository {
  private readonly http = inject(HttpClient);
  private readonly path = 'api/Stores';

  list(query: PageQuery, filter?: StoreFilter): Observable<PagedResult<Store>> {
    const params = new HttpParams({
      fromObject: storeQueryParams(query) as Record<string, string | number>,
    });

    return this.http.get<ApiPagedResponse<StoreDto>>(this.path, { params }).pipe(
      map(mapPagedStores),
      map((result) => applyStoreFilter(result, filter)),
    );
  }

  listInactive(query: PageQuery, search?: string): Observable<PagedResult<Store>> {
    const params = new HttpParams({
      fromObject: storeQueryParams({ ...query, search }) as Record<string, string | number>,
    });

    return this.http
      .get<ApiPagedResponse<StoreDto>>(`${this.path}/GetstoresNotActiveWithReason`, { params })
      .pipe(map(mapPagedStores));
  }

  listSignUsers(query: PageQuery): Observable<PagedResult<StoreSignUser>> {
    const params = new HttpParams({
      fromObject: storeQueryParams(query) as Record<string, string | number>,
    });

    return this.http
      .get<ApiPagedResponse<StoreSignUserDto>>('api/stores/storeUserData', { params })
      .pipe(map(mapPagedSignUsers));
  }

  listOrders(query: PageQuery): Observable<PagedResult<StoreOrder>> {
    const params = new HttpParams({
      fromObject: storeQueryParams(query) as Record<string, string | number>,
    });

    return this.http
      .get<ApiPagedResponse<StoreOrderDto>>(`${this.path}/all-users-Orders`, { params })
      .pipe(map(mapPagedStoreOrders));
  }

  listPriorities(query: PageQuery): Observable<PagedResult<StorePriority>> {
    const params = new HttpParams({
      fromObject: storeQueryParams(query) as Record<string, string | number>,
    });

    return this.http
      .get<ApiPagedResponse<StorePriorityDto>>('api/store-priorities', { params })
      .pipe(map(mapPagedPriorities));
  }

  getById(id: number): Observable<Store> {
    return this.http.get<StoreDto>(`${this.path}/${id}`).pipe(map(mapStoreDto));
  }

  getPendingChanges(id: number): Observable<Store> {
    return this.http.get<StoreDto>(`${this.path}/${id}/store-changes`).pipe(map(mapStoreDto));
  }

  getVoucherUsage(id: number): Observable<StoreVoucherUsage> {
    return this.http
      .get<StoreVoucherUsageDto>(`${this.path}/${id}/voucher-usage`)
      .pipe(map(mapStoreVoucherUsageDto));
  }

  updateActive(id: number, active: boolean): Observable<void> {
    return this.http.put<void>(`${this.path}/${id}/status`, { status: active });
  }

  updateStatus(id: number, storeStatus: string): Observable<void> {
    return this.http.put<void>(`${this.path}/${id}/store-status`, { storeStatus });
  }

  updateCategories(id: number, categoryIds: readonly number[]): Observable<void> {
    return this.http.put<void>(`${this.path}/${id}/store-categories`, {
      CategoryIds: [...categoryIds],
    });
  }

  updatePriority(id: number, priorityId: number): Observable<void> {
    return this.http.put<void>(`${this.path}/${id}/priority`, { priorityId });
  }

  updateCompanyShare(id: number, companyShare: number): Observable<void> {
    return this.http.put<void>(`${this.path}/${id}/company-share`, { companyShare });
  }

  updateLight(id: number, payload: StoreLightUpdate): Observable<void> {
    return this.http.put<void>(`${this.path}/UpdateLight/${id}`, {
      name: payload.name,
      description: payload.description,
      webSiteUrl: payload.webSiteUrl,
      mapUrl: payload.mapUrl,
      vouchers: payload.vouchers.map((voucher) => ({
        voucherId: voucher.id,
        isOnlineVoucher: voucher.isOnline,
        code: voucher.code,
        value: voucher.value,
        percent: voucher.percent,
        validityInDays: voucher.validityInDays,
      })),
      logoImage: payload.logoImage ?? '',
      isShowbuy: payload.isShowBuy,
      storeType: payload.storeType,
      note: payload.note,
      isCertified: payload.isCertified,
    });
  }

  updateSignAction(id: number, payload: StoreSignActionUpdate): Observable<void> {
    return this.http.put<void>(`${this.path}/UpdateStoreAction/${id}`, {
      status: payload.status,
      descriptionEntered: payload.descriptionEntered,
      onsuccessEntered: payload.onSuccessEntered,
    });
  }

  approveChanges(id: number): Observable<void> {
    return this.http.put<void>(`${this.path}/${id}/changes/approve`, {});
  }

  addExtraImage(id: number, extraImage: string): Observable<void> {
    return this.http.post<void>(`${this.path}/${id}/extra-images`, { extraImage });
  }

  removeExtraImage(id: number, extraImage: string): Observable<void> {
    return this.http.delete<void>(`${this.path}/${id}/extra-images/${extraImage}`);
  }

  deleteLocation(storeId: number, locationId: number): Observable<void> {
    return this.http.delete<void>(`${this.path}/${storeId}/store-locations/${locationId}`);
  }

  notifyOwner(ownerUserId: number, title: string, notificationText: string): Observable<void> {
    return this.http.post<void>('api/Notifications', {
      sendToAll: false,
      userIds: [ownerUserId],
      title,
      notificationText,
    });
  }
}
